import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ImagePlus, Loader2, Plus, Trash2, Upload, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  createProduct, updateProduct, fetchProductForEdit, getProductFileUploadUrl, uploadToSignedUrl, goodsProcessingFee,
  type GoodsOption, type ProductDownload,
} from '@/services/creatorApi';

// Products: a physical thing that ships (a T-shirt, a poster, a book) or a digital
// download (a script pack, a workbook). No commission: the creator pays payment
// processing (2.9% + 30c) and keeps the rest. A physical order's money is released
// when they mark it shipped. Sales tax is theirs to handle. Joe, Oct 25 2026.

const INK = 'var(--hj-ink)';
const MUTED = 'var(--hj-ink-2)';
const imgSrc = (k?: string | null) => !k ? '' : (k.startsWith('http') || k.startsWith('/api/') ? k : `/api/s3/${k}`);
const card: React.CSSProperties = { background: '#fff', border: '1px solid var(--hj-hairline-soft)', borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 };
const label: React.CSSProperties = { fontSize: 15, fontWeight: 700, color: INK };
const help: React.CSSProperties = { fontSize: 13.5, color: MUTED, margin: 0, lineHeight: 1.45 };
const field: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--hj-hairline)', fontSize: 15, fontFamily: 'inherit', background: '#fff', color: INK };
const pill = (on: boolean): React.CSSProperties => ({ minHeight: 44, padding: '10px 18px', borderRadius: 999, fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer', background: on ? INK : '#fff', color: on ? '#fff' : INK, border: `1px solid ${on ? INK : 'var(--hj-hairline)'}` });
const quiet: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 40, padding: '8px 14px', borderRadius: 10, border: '1px solid var(--hj-hairline)', background: '#fff', color: INK, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' };
const toCents = (t: string) => { const n = Math.round(parseFloat(String(t).replace(/[^0-9.]/g, '')) * 100); return Number.isFinite(n) ? n : 0; };
const dollars = (c: number) => (c / 100).toFixed(2);

const GoodsBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { productId } = useParams<{ productId?: string }>();
  const editing = !!productId && productId !== 'new';

  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [kind, setKind] = useState<'physical' | 'digital'>('physical');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priceText, setPriceText] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [useOptions, setUseOptions] = useState(false);
  const [optionName, setOptionName] = useState('Size');
  const [options, setOptions] = useState<GoodsOption[]>([{ key: '', label: 'S', stock: 0 }, { key: '', label: 'M', stock: 0 }, { key: '', label: 'L', stock: 0 }, { key: '', label: 'XL', stock: 0 }]);
  const [stockText, setStockText] = useState('');
  const [shippingText, setShippingText] = useState('');
  const [downloads, setDownloads] = useState<ProductDownload[]>([]);
  const [published, setPublished] = useState(true);
  const [uploading, setUploading] = useState<'photo' | 'file' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) return;
    fetchProductForEdit(productId!).then((p) => {
      setTitle(p.title || ''); setDescription(p.description || ''); setPriceText(p.price ? dollars(p.price) : '');
      setPhotos(p.galleryImages?.length ? p.galleryImages : (p.coverImage ? [p.coverImage] : []));
      setPublished(!!p.published); setDownloads(p.downloads || []);
      const g = p.goods;
      if (g) {
        setKind(g.kind);
        if (g.options?.length) { setUseOptions(true); setOptionName(g.optionName || 'Size'); setOptions(g.options); }
        else { setUseOptions(false); setStockText(g.stock === null || g.stock === undefined ? '' : String(g.stock)); }
        setShippingText(g.shippingCents ? dollars(g.shippingCents) : '');
      }
    }).catch((e) => setError((e as Error).message)).finally(() => setLoading(false));
  }, [productId]);

  const upload = async (file: File, what: 'photo' | 'file') => {
    setUploading(what); setError(null);
    try {
      const { uploadUrl, key } = await getProductFileUploadUrl(`${what === 'photo' ? 'goods-photo' : 'goods-file'}-${file.name}`, file.type);
      await uploadToSignedUrl(uploadUrl, file);
      if (what === 'photo') setPhotos((list) => [...list, key].slice(0, 6));
      else setDownloads((list) => [...list, { id: Math.random().toString(36).slice(2, 12), title: file.name.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ').trim() || 'Download', s3Key: key, bytes: file.size }]);
    } catch (e) { setError((e as Error).message); }
    setUploading(null);
  };

  const price = toCents(priceText);
  const shipping = kind === 'physical' ? toCents(shippingText) : 0;
  const example = price + shipping;
  const fee = example > 0 ? goodsProcessingFee(example) : 0;

  const save = async () => {
    setError(null);
    if (!title.trim()) { setError('Give it a name.'); return; }
    if (price < 100) { setError('The price has to be at least $1.00.'); return; }
    if (kind === 'digital' && published && !downloads.length) { setError('Add the file buyers download, or save it as a draft.'); return; }
    if (published && !photos.length) { setError('Add at least one photo, or save it as a draft.'); return; }
    setSaving(true);
    const goods = {
      kind,
      optionName: kind === 'physical' && useOptions ? optionName : null,
      options: kind === 'physical' && useOptions ? options.filter((o) => o.label.trim()) : [],
      stock: kind === 'physical' && !useOptions ? (stockText.trim() === '' ? null : Math.max(0, Math.floor(Number(stockText) || 0))) : null,
      shippingCents: shipping,
    };
    const payload = {
      title: title.trim(), description: description.trim(), price, productType: 'goods' as const, pricingMode: 'one_time' as const,
      coverImage: photos[0] || '', galleryImages: photos, downloads: kind === 'digital' ? downloads.map(({ id, title: t, s3Key, bytes }) => ({ id, title: t, s3Key, bytes })) : [],
      goods, published, videos: [],
    };
    try {
      if (editing) await updateProduct(productId!, payload);
      else await createProduct(payload);
      toast({ title: published ? 'Your product is live' : 'Saved as a draft' });
      navigate('/my-store');
    } catch (e) { setError((e as Error).message); }
    setSaving(false);
  };

  if (loading) return <main className="min-h-screen flex items-center justify-center" style={{ background: 'var(--hj-bg)' }}><Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} /></main>;

  return (
    <main className="mx-auto" style={{ maxWidth: 720, padding: '0 16px 96px', background: 'var(--hj-bg)', color: INK, minHeight: 'calc(100vh - 68px)' }}>
      <div style={{ padding: '20px 0 10px' }}>
        <button type="button" onClick={() => navigate('/my-store')} className="inline-flex items-center gap-1.5" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: INK, fontSize: 15, fontFamily: 'inherit', minHeight: 44 }}>
          <ChevronLeft size={16} /> My Store
        </button>
      </div>
      <h1 className="font-hj-display m-0" style={{ fontWeight: 800, fontSize: 'clamp(28px, 5vw, 38px)', letterSpacing: '-0.03em', marginBottom: 18 }}>{editing ? 'Edit product' : 'New product'}</h1>

      <div className="flex flex-col" style={{ gap: 16 }}>
        <section style={card}>
          <span style={label}>What are you selling?</span>
          <div className="flex flex-wrap" style={{ gap: 10 }}>
            <button type="button" style={pill(kind === 'physical')} onClick={() => setKind('physical')}>Something I ship</button>
            <button type="button" style={pill(kind === 'digital')} onClick={() => setKind('digital')}>A download</button>
          </div>
          <p style={help}>{kind === 'physical' ? 'A T-shirt, a poster, a book. Buyers give a US shipping address at checkout and you ship it.' : 'A script pack, a workbook, a PDF. Buyers download it right after they pay.'}</p>
        </section>

        <section style={card}>
          <label style={label} htmlFor="g-title">Name</label>
          <input id="g-title" style={field} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder={kind === 'physical' ? 'Hey Joe Class of 2026 T-shirt' : 'Comedy Monologue Pack'} />
          <label style={label} htmlFor="g-desc">Description</label>
          <textarea id="g-desc" style={{ ...field, minHeight: 110, resize: 'vertical' }} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What it is, what it's made of, sizing notes." />
        </section>

        <section style={card}>
          <span style={label}>Photos</span>
          <p style={help}>Up to 6. The first one is the main photo.</p>
          <div className="flex flex-wrap" style={{ gap: 10 }}>
            {photos.map((k, i) => (
              <div key={k} style={{ position: 'relative', width: 96, height: 96, borderRadius: 10, overflow: 'hidden', border: i === 0 ? `2px solid ${INK}` : '1px solid var(--hj-hairline)' }}>
                <img src={imgSrc(k)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button type="button" aria-label="Remove photo" onClick={() => setPhotos((l) => l.filter((x) => x !== k))} style={{ position: 'absolute', top: 4, right: 4, width: 26, height: 26, borderRadius: 13, border: 0, background: 'rgba(0,0,0,0.7)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={14} /></button>
              </div>
            ))}
            {photos.length < 6 && (
              <button type="button" disabled={!!uploading} onClick={() => photoInput.current?.click()} style={{ width: 96, height: 96, borderRadius: 10, border: '1px dashed var(--hj-hairline)', background: 'var(--hj-surface-1)', color: INK, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, fontSize: 13, fontFamily: 'inherit' }}>
                {uploading === 'photo' ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ImagePlus size={20} /> Add photo</>}
              </button>
            )}
          </div>
          <input ref={photoInput} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f, 'photo'); e.target.value = ''; }} />
        </section>

        <section style={card}>
          <label style={label} htmlFor="g-price">Price</label>
          <input id="g-price" style={{ ...field, maxWidth: 200 }} inputMode="decimal" value={priceText} onChange={(e) => setPriceText(e.target.value)} placeholder="25.00" />
          {kind === 'physical' && (
            <>
              <label style={label} htmlFor="g-ship">Shipping, per order</label>
              <input id="g-ship" style={{ ...field, maxWidth: 200 }} inputMode="decimal" value={shippingText} onChange={(e) => setShippingText(e.target.value)} placeholder="5.00 (leave empty for free shipping)" />
            </>
          )}
          <p style={help}>
            No commission. You pay payment processing, 2.9% + 30¢ per order{example > 0 ? `: on a $${dollars(example)} order that's $${dollars(fee)}, and you get $${dollars(example - fee)}` : ''}.
            {kind === 'physical' ? ' Your money is released when you mark the order shipped.' : ''} Sales tax is yours to handle.
          </p>
        </section>

        {kind === 'physical' && (
          <section style={card}>
            <span style={label}>How many do you have?</span>
            <div className="flex flex-wrap" style={{ gap: 10 }}>
              <button type="button" style={pill(!useOptions)} onClick={() => setUseOptions(false)}>One version</button>
              <button type="button" style={pill(useOptions)} onClick={() => setUseOptions(true)}>Sizes or options</button>
            </div>
            {!useOptions ? (
              <>
                <input aria-label="Quantity in stock" style={{ ...field, maxWidth: 200 }} inputMode="numeric" value={stockText} onChange={(e) => setStockText(e.target.value.replace(/[^0-9]/g, ''))} placeholder="Quantity" />
                <p style={help}>It shows as sold out at zero. Leave empty if you don’t want to track it.</p>
              </>
            ) : (
              <>
                <input aria-label="Option name" style={{ ...field, maxWidth: 240 }} value={optionName} onChange={(e) => setOptionName(e.target.value)} placeholder="Size" maxLength={30} />
                <div className="flex flex-col" style={{ gap: 8 }}>
                  {options.map((o, i) => (
                    <div key={i} className="flex items-center" style={{ gap: 8 }}>
                      <input aria-label="Option" style={{ ...field, flex: 1 }} value={o.label} onChange={(e) => setOptions((l) => l.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="M" maxLength={40} />
                      <input aria-label={`Quantity of ${o.label || 'this option'}`} style={{ ...field, width: 110 }} inputMode="numeric" value={String(o.stock)} onChange={(e) => setOptions((l) => l.map((x, j) => (j === i ? { ...x, stock: Math.max(0, Math.floor(Number(e.target.value.replace(/[^0-9]/g, '')) || 0)) } : x)))} />
                      <button type="button" aria-label="Remove option" onClick={() => setOptions((l) => l.filter((_, j) => j !== i))} style={{ ...quiet, padding: 8 }}><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
                {options.length < 20 && <button type="button" style={{ ...quiet, alignSelf: 'flex-start' }} onClick={() => setOptions((l) => [...l, { key: '', label: '', stock: 0 }])}><Plus size={16} /> Add an option</button>}
                <p style={help}>The number next to each is how many you have. Each one shows as sold out at zero.</p>
              </>
            )}
          </section>
        )}

        {kind === 'digital' && (
          <section style={card}>
            <span style={label}>The file buyers download</span>
            {downloads.map((d) => (
              <div key={d.id} className="flex items-center" style={{ gap: 8 }}>
                <input aria-label="File title" style={{ ...field, flex: 1 }} value={d.title} onChange={(e) => setDownloads((l) => l.map((x) => (x.id === d.id ? { ...x, title: e.target.value } : x)))} />
                <button type="button" aria-label="Remove file" onClick={() => setDownloads((l) => l.filter((x) => x.id !== d.id))} style={{ ...quiet, padding: 8 }}><Trash2 size={16} /></button>
              </div>
            ))}
            <button type="button" disabled={!!uploading} style={{ ...quiet, alignSelf: 'flex-start' }} onClick={() => fileInput.current?.click()}>
              {uploading === 'file' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload size={16} />} {downloads.length ? 'Add another file' : 'Upload the file'}
            </button>
            <input ref={fileInput} type="file" accept="application/pdf,image/*,application/zip,audio/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f, 'file'); e.target.value = ''; }} />
            <p style={help}>A PDF, a zip, images or audio. Buyers get it on their purchase page and in their confirmation email.</p>
          </section>
        )}

        <section style={{ ...card, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={label}>Live in my store</span>
          <div className="flex" style={{ gap: 8 }}>
            <button type="button" style={pill(published)} onClick={() => setPublished(true)}>Live</button>
            <button type="button" style={pill(!published)} onClick={() => setPublished(false)}>Draft</button>
          </div>
        </section>

        {error && <p style={{ ...help, color: 'var(--hj-pink-ink)', fontWeight: 600 }}>{error}</p>}
        <button type="button" disabled={saving || !!uploading} onClick={save} className="inline-flex items-center justify-center gap-2" style={{ minHeight: 52, borderRadius: 12, border: 0, background: INK, color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: saving ? 0.6 : 1 }}>
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : editing ? 'Save' : published ? 'Publish' : 'Save draft'}
        </button>
      </div>
    </main>
  );
};

export default GoodsBuilderPage;
